import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

/**
 * HeroA — "THE OBVIOUS": Linear/Arc-style product hero.
 *
 *   Huge centered headline + sub + CTAs, then a massive tilted
 *   mockup of the DAGGPT workspace floating below with live
 *   micro-animations inside (streaming chat, generating image,
 *   ticking provenance hash). White theme.
 */
export function HeroA_Product({ onSignInClick }) {
  const { isAuthenticated, issueSSOToken } = useAuth();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);
  const subRef = useRef(null);
  const ctaRef = useRef(null);
  const mockupRef = useRef(null);
  const [dgChars, setDgChars] = useState(0);
  const [showDesktopOnly, setShowDesktopOnly] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set([line1Ref.current, line2Ref.current, line3Ref.current], { yPercent: 110 });
      gsap.set([subRef.current, ctaRef.current],
        { autoAlpha: 0, y: 22 });
      gsap.set(mockupRef.current, {
        autoAlpha: 0, y: 80, scale: 0.96,
        transformOrigin: '50% 20%',
      });

      const tl = gsap.timeline({ defaults: { ease: 'expo.out' }, delay: 0.15 });
      tl.to([line1Ref.current, line2Ref.current, line3Ref.current],
        { yPercent: 0, duration: 1.3, stagger: 0.12 })
        .to(subRef.current, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.6')
        .to(ctaRef.current, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.7')
        .to(mockupRef.current, {
          autoAlpha: 1, y: 0, scale: 1,
          duration: 1.3, ease: 'expo.out',
        }, '-=0.8');

      /* Scroll: subtle parallax only — no tilt */
      gsap.to(mockupRef.current, {
        y: -40,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6,
        },
      });
    }, sectionRef);

    /* DAGGPT typewriter — starts after 'Meet' has revealed */
    const start = setTimeout(() => {
      let i = 0;
      const tick = () => {
        i += 1;
        setDgChars(i);
        if (i < 6) setTimeout(tick, 110);
      };
      tick();
    }, 950);

    return () => { clearTimeout(start); ctx.revert(); };
  }, []);

  return (
    <section ref={sectionRef} style={{
      position: 'relative',
      background: 'transparent',
      padding: 'clamp(120px, 14vh, 160px) clamp(24px, 5vw, 80px) 0',
      overflow: 'hidden',
    }}>
      {/* Backdrop */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `
          radial-gradient(ellipse 80% 50% at 50% 20%, rgba(167,139,250,0.10) 0%, transparent 60%),
          radial-gradient(ellipse 60% 40% at 50% 100%, rgba(124,58,237,0.06) 0%, transparent 60%)
        `,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'radial-gradient(circle, rgba(10,10,10,0.04) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        maskImage: 'radial-gradient(ellipse at 50% 30%, black 0%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative', zIndex: 2,
        maxWidth: '1360px', margin: '0 auto',
        textAlign: 'center',
      }}>
        {/* Headline — 3 lines */}
        <h1 style={{
          fontFamily: '"Nasalization", Georgia, serif',
          fontWeight: 300,
          fontSize: 'clamp(2.8rem, 7.5vw, 8.5rem)',
          lineHeight: 0.92,
          letterSpacing: '-0.035em',
          color: '#FFFFFF',
          margin: '0 auto',
          maxWidth: '14ch',
          fontVariationSettings: '"opsz" 144, "SOFT" 30',
        }}>
          <LineMask ref={line1Ref}>
            Meet&nbsp;<span className="dg-word">{'DAGGPT'.slice(0, dgChars)}<span className={`dg-caret${dgChars >= 6 ? ' dg-caret--gone' : ''}`}>▊</span></span>.
          </LineMask>
          <LineMask ref={line2Ref}>Create&nbsp;anything.</LineMask>
          <LineMask ref={line3Ref}>
            <em style={{
              fontStyle: 'italic', fontWeight: 400,
              fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 1',
              display: 'inline-block',
              paddingRight: '0.12em',
              paddingBottom: '0.08em',
            }}>Own it forever.</em>
          </LineMask>
        </h1>

        {/* Sub */}
        <p ref={subRef} style={{
          fontFamily: '"Inter", sans-serif',
          fontSize: 'clamp(15px, 1.15vw, 18px)',
          lineHeight: 1.6,
          color: '#A1A1AA',
          maxWidth: '540px',
          margin: '32px auto 0',
          fontWeight: 400,
        }}>
          One workspace for every frontier model — chat, image, music, video,
          code, smart contracts. Every output signed and anchored on
          <span style={{ color: '#7C3AED', fontWeight: 500 }}> DAGCHAIN</span>.
        </p>

        {/* CTAs */}
        <div ref={ctaRef} style={{
          display: 'inline-flex', gap: '12px',
          marginTop: '36px', flexWrap: 'wrap', justifyContent: 'center',
        }}>
          <a href="/workspace" style={ctaDark}
            onClick={() => {
              if (isAuthenticated) {
                navigate('/dashboard');
              } else {
                if (onSignInClick) onSignInClick('/dashboard');
              }
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#7C3AED'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#0A0A0A'; e.currentTarget.style.transform = 'none'; }}>
            Enter the workspace
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17L17 7M8 7h9v9" />
            </svg>
          </a>
          <a href="https://builder.daggpt.network" style={ctaViolet}
            onClick={async (e) => {
              if (window.innerWidth < 768) {
                e.preventDefault();
                setShowDesktopOnly(true);
                return;
              }
              if (!isAuthenticated) {
                e.preventDefault();
                if (onSignInClick) onSignInClick('https://builder.daggpt.network');
              } else {
                // SSO jump
                e.preventDefault();
                try {
                  const res = await issueSSOToken('builder');
                  if (res?.url) {
                    window.location.href = res.url;
                  } else {
                    window.location.href = 'https://builder.daggpt.network';
                  }
                } catch {
                  window.location.href = 'https://builder.daggpt.network';
                }
              }
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#7C3AED';
              e.currentTarget.style.color = '#FFFFFF';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 28px -10px rgba(124,58,237,0.55)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(124,58,237,0.08)';
              e.currentTarget.style.color = '#5B21B6';
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H10v7H6a2 2 0 0 1-2-2z" />
            </svg>
            Build applications
          </a>
        </div>

        {/* Workspace mockup */}
        <div className="mobile-hide-mockup" style={{
          marginTop: 'clamp(60px, 7vw, 100px)',
          paddingBottom: '60px',
        }}>
          <div ref={mockupRef} style={{
            margin: '0 auto',
            maxWidth: '1240px',
          }}>
            <WorkspaceMockup />
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 991px) {
          .mobile-hide-mockup {
            display: none !important;
          }
        }
        .dg-word {
          display: inline-block;
          background-image: linear-gradient(120deg, #7C3AED 0%, #A78BFA 25%, #EC4899 50%, #A78BFA 75%, #7C3AED 100%);
          background-size: 300% 100%;
          -webkit-background-clip: text;
                  background-clip: text;
          -webkit-text-fill-color: transparent;
                  color: transparent;
          animation: dgShimmer 6s ease-in-out infinite;
        }
        .dg-caret {
          display: inline-block;
          margin-left: 0.04em;
          color: #7C3AED;
          -webkit-text-fill-color: #7C3AED;
          animation: dgCaret 0.85s steps(1) infinite;
        }
        .dg-caret--gone { opacity: 0; animation: none; }
        @keyframes dgCaret { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        @keyframes dgShimmer {
          0%, 100% { background-position:   0% 50%; }
          50%      { background-position: 100% 50%; }
        }
      `}</style>

      <DesktopOnlyPopup isOpen={showDesktopOnly} onClose={() => setShowDesktopOnly(false)} />
    </section>
  );
}

function DesktopOnlyPopup({ isOpen, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'rgba(247, 245, 240, 0.72)',
          backdropFilter: 'blur(16px)',
        }}>
          {/* Backdrop click to close */}
          <div
            onClick={onClose}
            style={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
          />

          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 30 }}
            transition={{ type: 'spring', damping: 26, stiffness: 290 }}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '380px',
              background: '#FAF8F2',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              borderRadius: '40px',
              padding: '30px 24px 24px',
              boxShadow: 'inset 4px 4px 8px rgba(255, 255, 255, 0.9), inset -4px -4px 8px rgba(184, 174, 155, 0.22), 0 24px 60px rgba(120, 110, 90, 0.12)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              zIndex: 2,
            }}
          >

            <h3 style={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '21px',
              fontWeight: 800,
              color: '#3A3835',
              margin: '0 0 12px',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
            }}>
              Desktop Studio Required
            </h3>

            <p style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '13.5px',
              lineHeight: 1.6,
              color: '#7C7872',
              margin: '0 0 28px',
              fontWeight: 500,
            }}>
              To guarantee a top-tier visual workspace for designing, coding, and building full-scale decentralized applications, the <span style={{ color: '#7665EC', fontWeight: 600 }}>Creator Studio</span> is optimized exclusively for desktop screens.
            </p>

            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '15px',
                borderRadius: '99px',
                background: 'linear-gradient(135deg, #A294F9 0%, #7665EC 100%)',
                color: '#FFFFFF',
                border: 'none',
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '14px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 10px 24px rgba(118, 101, 236, 0.35), inset 2px 2px 4px rgba(255, 255, 255, 0.4), inset -2px -2px 4px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.2s ease-in-out',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(118, 101, 236, 0.45), inset 2px 2px 4px rgba(255, 255, 255, 0.4), inset -2px -2px 4px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 10px 24px rgba(118, 101, 236, 0.35), inset 2px 2px 4px rgba(255, 255, 255, 0.4), inset -2px -2px 4px rgba(0, 0, 0, 0.15)';
              }}
            >
              Acknowledged
            </button>

            <span
              onClick={onClose}
              style={{
                marginTop: '18px',
                fontSize: '11px',
                color: '#A8A297',
                cursor: 'pointer',
                fontWeight: 800,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#7665EC'}
              onMouseLeave={e => e.currentTarget.style.color = '#A8A297'}
            >
              Return to Hub
            </span>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* ═══════════════════ WORKSPACE MOCKUP ═══════════════════ */
const ITEMS = [
  { t: 'Aurora GT · hypercar', m: 'Flux Pro 1.1', kind: 'image' },
  { t: 'Whitepaper v2 draft', m: 'Claude 3.5', kind: 'chat' },
  { t: 'Onboarding soundtrack', m: 'Suno v4', kind: 'music' },
  { t: 'Brand palette studies', m: 'Ideogram 3', kind: 'image' },
  { t: 'Product reel draft', m: 'Runway Gen-3', kind: 'video' },
];

function WorkspaceMockup() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = ITEMS[activeIdx];
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      background: 'rgba(20, 20, 25, 0.45)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      borderRadius: '24px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow:
        '0 4px 6px rgba(0,0,0,0.1),' +
        '0 12px 28px -8px rgba(0,0,0,0.3),' +
        '0 60px 120px -40px rgba(0,0,0,0.5)',
      overflow: 'hidden',
      textAlign: 'left',
      position: 'relative',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '260px 1fr 320px',
        minHeight: isMobile ? 'auto' : '520px',
        background: 'transparent',
      }}>
        {!isMobile && <Sidebar activeIdx={activeIdx} onSelect={setActiveIdx} />}
        <MainCanvas active={active} isMobile={isMobile} />
        {!isMobile && <ProvenancePanel />}
      </div>

      <style>{`
        @keyframes scanMove  { 0% { top: 0%; opacity: 1; } 95% { top: 100%; opacity: 1; } 100% { top: 0%; opacity: 0; } }
        @keyframes progMove  { 0% { width: 62%; } 50% { width: 88%; } 100% { width: 62%; } }
        @keyframes rotSlow   { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes coreBeat  { 0%,100% { opacity: 0.75; } 50% { opacity: 1; } }
        @keyframes caretBlink{ 0%,50% { opacity: 1; } 51%,100% { opacity: 0; } }
      `}</style>
    </div>
  );
}

/* ── Browser chrome ── */
function BrowserChrome({ isMobile }) {
  return (
    <div style={{
      height: '40px',
      borderBottom: '1px solid rgba(10,10,10,0.06)',
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '0 16px',
      background: 'linear-gradient(180deg, #FAFAF9 0%, #F4F4F5 100%)',
    }}>
      <div style={{ display: 'flex', gap: '7px' }}>
        {['#FF5F56', '#FFBD2E', '#27C93F'].map(c => (
          <div key={c} style={{
            width: 11, height: 11, borderRadius: '50%',
            background: c,
            boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.15)',
          }} />
        ))}
      </div>
      <div style={{
        flex: 1, maxWidth: '360px',
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '4px 12px', borderRadius: '7px',
        background: '#FFFFFF',
        border: '1px solid rgba(10,10,10,0.06)',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '10.5px', color: '#71717A',
      }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5">
          <path d="M12 2 L2 7 v7 c0 5 4 8 10 8 s10-3 10-8 V7 z" />
        </svg>
        daggpt.network/workspace
      </div>
      {!isMobile && (
        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
          {['≡', '⌕', '⤓'].map(s => (
            <div key={s} style={{
              width: 24, height: 24, borderRadius: '6px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#A1A1AA', fontSize: '13px',
              background: 'transparent',
            }}>{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Sidebar ── */
function Sidebar({ activeIdx, onSelect }) {
  const TIMES = ['now', '4m', '1h', '3h', 'yest.'];
  const RECENT = ITEMS.map((it, i) => ({ ...it, time: TIMES[i], idx: i }));
  const PINNED = [
    { t: 'DAGCHAIN L1 manual', m: 'Gemini 2.5', kind: 'chat', time: 'pin', idx: -1 },
  ];

  return (
    <aside style={{
      borderRight: '1px solid rgba(255,255,255,0.05)',
      background: 'transparent',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Brand header + user */}
      <div style={{
        padding: '14px 14px 10px',
        display: 'flex', alignItems: 'center', gap: '10px',
        borderBottom: '1px solid rgba(10,10,10,0.05)',
      }}>
        <div style={{
          fontFamily: '"Nasalization", serif', fontStyle: 'italic',
          fontWeight: 400, fontSize: '18px', letterSpacing: '-0.02em',
          color: '#FFFFFF', flex: 1,
        }}>DAG<span style={{ color: '#A78BFA' }}>GPT</span></div>
        <span style={{
          padding: '2px 6px', borderRadius: '4px',
          background: 'rgba(124,58,237,0.1)',
          border: '1px solid rgba(124,58,237,0.2)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '8.5px', fontWeight: 700,
          color: '#5B21B6', letterSpacing: '0.15em',
        }}>BETA</span>
        <div style={{
          width: 26, height: 26, borderRadius: '50%',
          background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
          color: '#FFFFFF', fontSize: '10px', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: '"Inter", sans-serif',
        }}>AD</div>
      </div>

      {/* Search */}
      <div style={{ padding: '12px 14px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '7px 10px', borderRadius: '8px',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <span style={{
            flex: 1, fontFamily: '"Inter", sans-serif',
            fontSize: '12px', color: '#A1A1AA',
          }}>Search generations…</span>
          <span style={{
            background: 'rgba(255,255,255,0.1)',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '9px', color: 'rgba(255,255,255,0.5)',
          }}>⌘K</span>
        </div>
      </div>

      {/* New gen button */}
      <div style={{ padding: '0 14px 14px' }}>
        <button style={{
          width: '100%',
          padding: '10px 12px',
          background: 'rgba(255,255,255,0.05)', color: '#FFFFFF',
          border: '1px solid rgba(255,255,255,0.08)', borderRadius: '9px',
          fontFamily: '"Inter", sans-serif',
          fontSize: '12.5px', fontWeight: 500,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          transition: 'background 0.2s',
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          New generation
          <span style={{
            marginLeft: 'auto',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '9px', opacity: 0.5,
          }}>⌘N</span>
        </button>
      </div>

      {/* Pinned */}
      <SidebarSection label="Pinned" count={PINNED.length}>
        {PINNED.map((c, i) => <ChatItem key={`p${i}`} {...c} />)}
      </SidebarSection>

      {/* Recent */}
      <SidebarSection label="Recent" count={RECENT.length}>
        {RECENT.map(c => (
          <ChatItem key={c.idx} {...c}
            active={c.idx === activeIdx}
            onClick={() => onSelect(c.idx)}
          />
        ))}
      </SidebarSection>

      {/* Footer — storage */}
      <div style={{
        marginTop: 'auto',
        padding: '12px 14px',
        borderTop: '1px solid rgba(255,255,255,0.05)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '9.5px', color: 'rgba(255,255,255,0.6)',
          letterSpacing: '0.1em', marginBottom: '6px',
        }}>
          <span>STORAGE</span>
          <span style={{ color: '#FFFFFF' }}>8.2 / 50 GB</span>
        </div>
        <div style={{
          height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.1)',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: '16%',
            background: 'linear-gradient(90deg, #7C3AED, #A78BFA)',
          }} />
        </div>
      </div>
    </aside>
  );
}

function SidebarSection({ label, count, children }) {
  return (
    <div style={{ padding: '0 14px 10px' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '9px', fontWeight: 600,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.6)',
        padding: '8px 2px',
      }}>
        <span>{label}</span>
        <span style={{ color: 'rgba(255,255,255,0.3)' }}>{count}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

const KIND_ICON = {
  image: { bg: 'rgba(219,39,119,0.15)', fg: '#F472B6', p: <path d="M3 17l5-5 4 4 3-3 6 6 M3 3h18v18H3z M15 7h.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" /> },
  chat: { bg: 'rgba(124,58,237,0.15)', fg: '#A78BFA', p: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeWidth="2" fill="none" strokeLinejoin="round" /> },
  music: { bg: 'rgba(5,150,105,0.15)', fg: '#34D399', p: <path d="M9 18V5l12-2v13 M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0z M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" strokeWidth="2" fill="none" strokeLinejoin="round" /> },
  contract: { bg: 'rgba(217,119,6,0.15)', fg: '#FBBF24', p: <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M8 13h8 M8 17h6" strokeWidth="2" fill="none" strokeLinejoin="round" /> },
  video: { bg: 'rgba(37,99,235,0.15)', fg: '#60A5FA', p: <path d="M23 7l-7 5 7 5V7z M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" strokeWidth="2" fill="none" strokeLinejoin="round" /> },
};

function ChatItem({ t, m, kind, time, active, onClick }) {
  const icon = KIND_ICON[kind] || KIND_ICON.chat;
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        padding: '9px 10px 9px 11px',
        borderRadius: '8px',
        marginBottom: '2px',
        background: active ? 'rgba(255,255,255,0.05)' : 'transparent',
        border: active ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '9px',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {active && (
        <div style={{
          position: 'absolute', left: -1, top: 6, bottom: 6,
          width: 2, borderRadius: 2,
          background: '#7C3AED',
        }} />
      )}
      <div style={{
        width: 22, height: 22, borderRadius: '6px',
        background: icon.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="11" height="11" viewBox="0 0 24 24" stroke={icon.fg}>
          {icon.p}
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '12.5px', fontWeight: 500,
          color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{t}</div>
        <div style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '9px', color: 'rgba(255,255,255,0.5)',
          letterSpacing: '0.08em', marginTop: '1px',
        }}>{m}</div>
      </div>
      <span style={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '9px', color: 'rgba(255,255,255,0.5)',
      }}>{time}</span>
    </div>
  );
}

/* ── Car photo pool (shared by main preview + variants) ── */
const CAR_URLS = [
  '/images/car1.jpg',
  '/images/car2.jpg',
  '/images/car3.jpg',
  '/images/car4.jpg',
];
const CAR_THUMBS = CAR_URLS;

/* ── Main canvas router ── */
function MainCanvas({ active, isMobile }) {
  if (active.kind === 'chat') return <ChatCanvas active={active} isMobile={isMobile} />;
  if (active.kind === 'music') return <MusicCanvas active={active} isMobile={isMobile} />;
  if (active.kind === 'video') return <VideoCanvas active={active} isMobile={isMobile} />;
  return <ImageCanvas active={active} isMobile={isMobile} />;
}

/* ── Image canvas (default) ── */
function ImageCanvas({ active, isMobile }) {
  const [variant, setVariant] = useState(0);
  const [progress, setProgress] = useState(8);

  /* Reset when switching between image items */
  useEffect(() => { setVariant(0); setProgress(8); }, [active]);

  /* Progress loop: 8 → 100, hold 6.5s, reset */
  useEffect(() => {
    let to;
    if (progress < 100) {
      to = setTimeout(() => setProgress(p => p + 1), 120);
    } else {
      to = setTimeout(() => setProgress(8), 6500);
    }
    return () => clearTimeout(to);
  }, [progress]);

  const pickVariant = i => { setVariant(i); setProgress(8); };
  const isDone = progress >= 100;

  return (
    <main style={{ padding: isMobile ? '12px 14px' : '18px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Breadcrumb */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '9.5px', letterSpacing: '0.18em',
        textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)',
      }}>
        <span>Workspace</span>
        <span>/</span>
        <span>Recent</span>
        <span>/</span>
        <span style={{ color: '#7C3AED' }}>{active.t}</span>
      </div>

      {/* Title bar */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '12px',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: '"Nasalization", serif', fontStyle: 'italic', fontWeight: 400,
            fontSize: isMobile ? '18px' : '22px', letterSpacing: '-0.02em', color: '#FFFFFF',
            lineHeight: 1.1,
          }}>{active.t}</div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px',
            marginTop: '5px',
          }}>
            {/* Model selector pill */}
            <div style={pill}>
              <img src="/flux.svg" alt="" style={{ width: 12, height: 12 }} />
              <span style={{ fontWeight: 500 }}>{active.m}</span>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            <div style={{ ...pill, background: 'rgba(255,255,255,0.02)' }}>2048 × 1152</div>
            <div style={{ ...pill, background: 'rgba(255,255,255,0.02)' }}>Seed 8241</div>
          </div>
        </div>

        {/* Status and Action Buttons row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          marginTop: isMobile ? '4px' : 0,
        }}>
          {/* Status pill — flips to COMPLETE at 100% */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 10px', borderRadius: '999px',
            background: isDone ? 'rgba(124,58,237,0.1)' : 'rgba(5,150,105,0.1)',
            border: `1px solid ${isDone ? 'rgba(124,58,237,0.2)' : 'rgba(5,150,105,0.2)'}`,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '9px', fontWeight: 700,
            color: isDone ? '#5B21B6' : '#047857',
            letterSpacing: '0.12em',
            transition: 'background 0.4s, border-color 0.4s, color 0.4s',
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: '50%',
              background: isDone ? '#7C3AED' : '#059669',
              boxShadow: `0 0 6px ${isDone ? '#7C3AED' : '#059669'}`,
              animation: isDone ? 'none' : 'coreBeat 1.4s ease-in-out infinite',
            }} />
            {isDone ? 'COMPLETE ✓' : `GENERATING ${progress}%`}
          </div>

          {/* Icon buttons */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { t: 'Share', p: 'M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7 M16 6l-4-4-4 4 M12 2v13' },
              { t: 'Download', p: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3' },
              { t: 'More', p: 'M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z' },
            ].map(b => (
              <button key={b.t} style={iconBtn} title={b.t}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={b.p} />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Progress bar — tracks real progress state */}
      <div style={{
        height: 3, borderRadius: 2,
        background: 'rgba(124,58,237,0.12)',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #7C3AED, #A78BFA, #EC4899)',
          boxShadow: '0 0 10px rgba(124,58,237,0.55)',
          transition: 'width 0.25s ease-out',
        }} />
      </div>

      {/* Generation preview — supercar photo with progressive reveal */}
      <div style={{
        aspectRatio: '16 / 9',
        borderRadius: '14px',
        position: 'relative', overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(0,0,0,0.4)',
      }}>
        {/* Image — starts blurry & desaturated, sharpens as progress rises */}
        <img
          key={variant}
          src={CAR_URLS[variant]}
          alt="Aurora GT hypercar"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            display: 'block',
            filter: `blur(${Math.max(0, (100 - progress) * 0.22)}px) saturate(${0.55 + progress * 0.005})`,
            transform: `scale(${1 + (100 - progress) * 0.0012})`,
            transition: 'filter 0.3s ease-out, transform 0.3s ease-out',
          }}
          loading="eager"
        />

        {/* Diffusion noise veil — fades as progress increases */}
        <div style={{
          position: 'absolute', inset: 0,
          opacity: Math.max(0, (100 - progress) / 140),
          background: `
            repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 1px, transparent 1px 3px),
            repeating-linear-gradient(-45deg, rgba(124,58,237,0.08) 0 1px, transparent 1px 4px)
          `,
          mixBlendMode: 'screen',
          pointerEvents: 'none',
          transition: 'opacity 0.3s ease-out',
        }} />

        {/* Curtain reveal — white gradient sweeps down as progress hits 100% */}
        <div style={{
          position: 'absolute', left: 0, right: 0, top: 0,
          height: `${100 - progress}%`,
          background: 'linear-gradient(180deg, rgba(245,243,255,0.92) 0%, rgba(221,214,254,0.75) 60%, rgba(124,58,237,0.4) 100%)',
          borderBottom: '1px solid rgba(124,58,237,0.45)',
          boxShadow: '0 8px 30px rgba(124,58,237,0.25)',
          pointerEvents: 'none',
          transition: 'height 0.25s ease-out',
        }} />

        {/* violet tint overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(124,58,237,0.06) 0%, transparent 35%, transparent 70%, rgba(10,10,10,0.25) 100%)',
          pointerEvents: 'none',
        }} />

        {/* scan-line sits at the curtain edge — fades out at 100% */}
        <div style={{
          position: 'absolute', left: 0, right: 0,
          top: `${100 - progress}%`,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #7C3AED 30%, #FFFFFF 50%, #7C3AED 70%, transparent)',
          boxShadow: '0 0 16px rgba(124,58,237,0.9)',
          opacity: isDone ? 0 : 1,
          pointerEvents: 'none',
          transition: 'top 0.25s ease-out, opacity 0.4s ease-out',
        }} />

        {/* corner timecode — flips to ANCHORED stamp at 100% */}
        <div style={{
          position: 'absolute', bottom: 10, left: 12,
          padding: '3px 8px', borderRadius: '4px',
          background: isDone ? 'rgba(124,58,237,0.85)' : 'rgba(10,10,10,0.55)',
          color: '#FFFFFF',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '9px', letterSpacing: '0.14em',
          backdropFilter: 'blur(4px)',
          transition: 'background 0.4s ease-out',
        }}>
          {isDone
            ? '✓ ANCHORED · 0x9f86…f00a'
            : `STEP ${Math.round(progress / 2)} / 50 · ${(progress * 0.04).toFixed(1)}s`}
        </div>
      </div>

      {/* Variant thumbnails — clickable, switches main preview */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: isMobile ? '6px' : '8px',
      }}>
        {CAR_THUMBS.map((url, i) => {
          const active = variant === i;
          return (
            <button
              key={i}
              onClick={() => pickVariant(i)}
              style={{
                aspectRatio: '1 / 1',
                borderRadius: '8px',
                border: active ? '1.5px solid #7C3AED' : '1px solid rgba(255,255,255,0.1)',
                position: 'relative', overflow: 'hidden',
                cursor: 'pointer',
                background: 'rgba(0,0,0,0.4)',
                padding: 0,
                transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
                boxShadow: active ? '0 4px 16px -4px rgba(124,58,237,0.45)' : 'none',
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.borderColor = 'rgba(124,58,237,0.5)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.transform = 'none';
                }
              }}
            >
              <img src={url} alt="" style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%', objectFit: 'cover',
              }} />
              <span style={{
                position: 'absolute', top: 5, left: 6, zIndex: 2,
                padding: '2px 5px', borderRadius: '3px',
                background: active ? '#7C3AED' : 'rgba(10,10,10,0.55)',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '8px', fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '0.12em',
                backdropFilter: 'blur(4px)',
              }}>V{i + 1}</span>
            </button>
          );
        })}
      </div>

      {/* Prompt bar */}
      <div style={{
        marginTop: 'auto',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
        padding: isMobile ? '8px 10px' : '11px 12px',
        display: 'flex', alignItems: 'center', gap: '10px',
        background: 'rgba(255,255,255,0.03)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
      }}>
        <Sparkle size={11} violet />
        <span style={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '12.5px', color: '#FFFFFF', flex: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{getPrompt(active)}</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button style={iconBtn} title="Attach">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round">
              <path d="M21 10l-10 10a5 5 0 0 1-7-7l10-10a3 3 0 0 1 4 4l-9 9a1 1 0 0 1-2-2l8-8" />
            </svg>
          </button>
          <button style={{
            ...iconBtn,
            background: '#FFFFFF', border: 'none',
          }} title="Send">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13 M22 2l-7 20-4-9-9-4z" />
            </svg>
          </button>
        </div>
      </div>
    </main>
  );
}

/* ─────────────────────────────────────────────────────────
   ALTERNATE CANVASES (chat / music / video)
   Each shares the same outer chrome shell.
   ───────────────────────────────────────────────────────── */

const PROMPTS = {
  'Aurora GT · hypercar': 'Aurora GT hypercar, carbon fiber body, sunset rim light, cinematic 35mm…',
  'Whitepaper v2 draft': 'Rewrite section 2.4 to clarify our DAG consensus model for non-technical readers…',
  'Onboarding soundtrack': 'Cinematic intro, 80 BPM, warm analog synth pads, soft piano, 90 seconds…',
  'Brand palette studies': 'Six brand palettes for a luxury automotive client, cool violet bias…',
  'Product reel draft': '15-second product reel — slow dolly around a parked Aurora GT at dusk…',
};
const getPrompt = active => PROMPTS[active.t] || '';

/* Shared chrome around any kind-specific preview */
function CanvasShell({ active, children, sizeLabel = '—', extraPill, isMobile }) {
  return (
    <main style={{ padding: isMobile ? '12px 14px' : '18px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '9.5px', letterSpacing: '0.18em',
        textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)',
      }}>
        <span>Workspace</span><span>/</span><span>Recent</span><span>/</span>
        <span style={{ color: '#7C3AED' }}>{active.t}</span>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '12px',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: '"Nasalization", serif', fontStyle: 'italic', fontWeight: 400,
            fontSize: isMobile ? '18px' : '22px', letterSpacing: '-0.02em', color: '#FFFFFF',
            lineHeight: 1.1,
          }}>{active.t}</div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px',
            marginTop: '5px',
          }}>
            <div style={pill}>
              <span style={{ fontWeight: 500 }}>{active.m}</span>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            <div style={{ ...pill, background: 'rgba(255,255,255,0.02)' }}>{sizeLabel}</div>
            {extraPill && <div style={{ ...pill, background: 'rgba(255,255,255,0.02)' }}>{extraPill}</div>}
          </div>
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 10px', borderRadius: '999px',
          background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '9px', fontWeight: 700,
          color: '#D8B4FE', letterSpacing: '0.12em',
          alignSelf: isMobile ? 'flex-start' : 'center',
          marginTop: isMobile ? '4px' : 0,
        }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#7C3AED', boxShadow: '0 0 6px #7C3AED' }} />
          READY
        </div>
      </div>

      {children}

      {/* Prompt bar */}
      <div style={{
        marginTop: 'auto',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
        padding: isMobile ? '8px 10px' : '11px 12px',
        display: 'flex', alignItems: 'center', gap: '10px',
        background: 'rgba(255,255,255,0.03)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
      }}>
        <Sparkle size={11} violet />
        <span style={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '12.5px', color: '#FFFFFF', flex: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{getPrompt(active)}</span>
        <button style={{
          width: 26, height: 26, borderRadius: '6px',
          background: '#FFFFFF', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
        }} title="Send">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 2L11 13 M22 2l-7 20-4-9-9-4z" />
          </svg>
        </button>
      </div>
    </main>
  );
}

/* ── Chat canvas ── */
function ChatCanvas({ active, isMobile }) {
  const [chars, setChars] = useState(0);
  const FULL =
    'The DAGCHAIN consensus model differs from a traditional blockchain by allowing many transactions to confirm in parallel. Instead of a single linear chain, finality emerges from a directed acyclic graph where each new vertex references several predecessors. The practical effect is sub-second finality without the throughput ceiling of sequential block production.';
  useEffect(() => {
    if (chars >= FULL.length) {
      const t = setTimeout(() => setChars(0), 5500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setChars(c => Math.min(FULL.length, c + 2)), 25);
    return () => clearTimeout(t);
  }, [chars]);

  return (
    <CanvasShell active={active} sizeLabel="Long-form" extraPill="Tone · Editorial" isMobile={isMobile}>
      <div style={{
        flex: 1, minHeight: 360,
        background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px', padding: '20px 22px',
        display: 'flex', flexDirection: 'column', gap: '14px',
        overflow: 'hidden',
      }}>
        {/* User msg */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
            color: '#FFF', fontSize: '10px', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>AD</div>
          <div style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px 10px 10px 2px', padding: '9px 12px',
            fontFamily: '"Inter", sans-serif', fontSize: '13px',
            color: '#FFFFFF', maxWidth: '88%',
          }}>{getPrompt(active)}</div>
        </div>

        {/* Assistant msg with typewriter */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <div style={{
            width: 26, height: 26, borderRadius: '7px',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Sparkle size={11} violet />
          </div>
          <div style={{
            background: 'rgba(124,58,237,0.1)',
            border: '1px solid rgba(124,58,237,0.25)',
            borderRadius: '10px 10px 2px 10px',
            padding: '11px 14px',
            fontFamily: '"Nasalization", serif', fontSize: '14px',
            fontWeight: 300, color: '#E9D5FF', lineHeight: 1.55,
            maxWidth: '92%',
          }}>
            {FULL.slice(0, chars)}
            <span style={{
              display: 'inline-block', width: '0.5em', height: '1em',
              background: '#7C3AED', verticalAlign: 'text-bottom',
              marginLeft: '2px',
              animation: 'caretBlink 0.9s steps(1) infinite',
            }} />
          </div>
        </div>

        {/* Status footer */}
        <div style={{
          marginTop: 'auto', display: 'flex', gap: '14px',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '9.5px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.16em',
        }}>
          <span>● STREAMING</span>
          <span>{Math.round((chars / FULL.length) * 1240)} / 1240 TOKENS</span>
          <span style={{ marginLeft: 'auto', color: '#7C3AED' }}>↳ {active.m}</span>
        </div>
      </div>
    </CanvasShell>
  );
}

/* ── Music canvas ── */
function MusicCanvas({ active, isMobile }) {
  const BARS = isMobile ? 32 : 64;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 90);
    return () => clearInterval(id);
  }, []);
  const heights = Array.from({ length: BARS }, (_, i) => {
    const phase = (i + tick) * 0.32;
    return 18 + Math.abs(Math.sin(phase) + Math.sin(phase * 0.5)) * 28;
  });

  return (
    <CanvasShell active={active} sizeLabel="01:30 · 80 BPM" extraPill="Cmaj · Cinematic" isMobile={isMobile}>
      <div style={{
        background: 'linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(31,15,58,0.4) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px', padding: isMobile ? '16px' : '24px',
        display: 'flex', flexDirection: 'column', gap: '20px',
        minHeight: 360,
      }}>
        {/* Now playing header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '12px',
            background: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px -8px rgba(124,58,237,0.6)',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#FFFFFF">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: '"Nasalization", serif', fontStyle: 'italic', fontWeight: 400,
              fontSize: '20px', color: '#FFFFFF', letterSpacing: '-0.01em',
            }}>{active.t}</div>
            <div style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '10px', color: 'rgba(255,255,255,0.5)',
              letterSpacing: '0.18em', marginTop: 4,
            }}>SUNO V4 · STEM-AWARE · 48 KHZ</div>
          </div>
          <span style={{
            padding: '4px 10px', borderRadius: '999px',
            background: 'rgba(124,58,237,0.25)',
            border: '1px solid rgba(167,139,250,0.5)',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '9px', fontWeight: 700, letterSpacing: '0.2em',
            color: '#C4B5FD',
          }}>● PLAYING</span>
        </div>

        {/* Waveform */}
        <div style={{
          flex: 1,
          display: 'flex', alignItems: 'center', gap: '3px',
          height: 100,
        }}>
          {heights.map((h, i) => (
            <div key={i} style={{
              flex: 1, height: `${h}%`,
              borderRadius: '2px',
              background: i < BARS * 0.42
                ? 'linear-gradient(180deg, #A78BFA, #7C3AED)'
                : 'rgba(255,255,255,0.18)',
              transition: 'height 0.18s ease-out',
            }} />
          ))}
        </div>

        {/* Timeline */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '10px', color: 'rgba(255,255,255,0.55)',
          letterSpacing: '0.14em',
        }}>
          <span>00:38</span>
          <span>STEMS · DRUMS · BASS · KEYS · PADS</span>
          <span>01:30</span>
        </div>
      </div>
    </CanvasShell>
  );
}

/* ── Video canvas ── */
function VideoCanvas({ active, isMobile }) {
  return (
    <CanvasShell active={active} sizeLabel="00:15 · 1080p" extraPill="24 fps · Cine" isMobile={isMobile}>
      <div style={{
        position: 'relative',
        aspectRatio: '16 / 9',
        borderRadius: '14px', overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(0,0,0,0.4)',
      }}>
        <img
          src="/images/car1.jpg"
          alt=""
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'cover',
            filter: 'saturate(0.85) contrast(1.05)',
          }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, transparent 50%, rgba(10,10,10,0.6) 100%)',
        }} />
        {/* Play button */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: isMobile ? 48 : 64, height: isMobile ? 48 : 64, borderRadius: '50%',
            background: 'rgba(255,255,255,0.95)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 32px -8px rgba(124,58,237,0.4)',
          }}>
            <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="#0A0A0A">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
        {/* Timeline overlay */}
        <div style={{
          position: 'absolute', left: isMobile ? 12 : 18, right: isMobile ? 12 : 18, bottom: isMobile ? 12 : 18,
          color: '#FFFFFF',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '10px', letterSpacing: '0.14em', marginBottom: 6,
          }}>
            <span>00:04</span><span>00:15</span>
          </div>
          <div style={{
            height: 3, borderRadius: 2,
            background: 'rgba(255,255,255,0.25)',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: '28%',
              background: 'linear-gradient(90deg, #7C3AED, #EC4899)',
            }} />
          </div>
        </div>
      </div>

      {/* Storyboard strip (Desktop Only) */}
      {!isMobile && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px',
        }}>
          {[1, 2, 3, 4, 1].map((n, i) => (
            <div key={i} style={{
              aspectRatio: '16 / 9',
              borderRadius: '6px',
              border: i === 1 ? '1.5px solid #7C3AED' : '1px solid rgba(255,255,255,0.08)',
              position: 'relative', overflow: 'hidden',
              background: '#0A0A0A',
            }}>
              <img src={`/images/car${n}.jpg`} alt="" style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%', objectFit: 'cover',
                opacity: 0.85,
              }} />
              <span style={{
                position: 'absolute', top: 3, left: 5,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '8px', fontWeight: 700, color: '#FFFFFF',
                padding: '1px 4px', borderRadius: '3px',
                background: 'rgba(10,10,10,0.65)',
                letterSpacing: '0.1em',
              }}>{`0${i + 1}`}</span>
            </div>
          ))}
        </div>
      )}
    </CanvasShell>
  );
}

/* ── SVG Glass Crystal Render (legacy, unused) ── */
function CrystalRender() {
  return (
    <svg viewBox="0 0 400 220" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <defs>
        <radialGradient id="studio" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#F5F3FF" />
          <stop offset="70%" stopColor="#DDD6FE" />
          <stop offset="100%" stopColor="#C4B5FD" />
        </radialGradient>
        <linearGradient id="facetL" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
          <stop offset="100%" stopColor="rgba(196,181,253,0.4)" />
        </linearGradient>
        <linearGradient id="facetR" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(196,181,253,0.85)" />
          <stop offset="100%" stopColor="rgba(139,92,246,0.35)" />
        </linearGradient>
        <linearGradient id="facetT" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,1)" />
          <stop offset="100%" stopColor="rgba(221,214,254,0.7)" />
        </linearGradient>
        <linearGradient id="facetB" x1="0.5" y1="1" x2="0.5" y2="0">
          <stop offset="0%" stopColor="rgba(124,58,237,0.7)" />
          <stop offset="100%" stopColor="rgba(167,139,250,0.55)" />
        </linearGradient>
        <radialGradient id="core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255,255,255,1)" />
          <stop offset="30%" stopColor="rgba(196,181,253,0.9)" />
          <stop offset="70%" stopColor="rgba(124,58,237,0.5)" />
          <stop offset="100%" stopColor="rgba(124,58,237,0)" />
        </radialGradient>
        <radialGradient id="shadow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(10,10,10,0.35)" />
          <stop offset="100%" stopColor="rgba(10,10,10,0)" />
        </radialGradient>
        <filter id="blur"><feGaussianBlur stdDeviation="3" /></filter>
      </defs>

      {/* Studio backdrop */}
      <rect width="400" height="220" fill="url(#studio)" />

      {/* Soft floor shadow */}
      <ellipse cx="200" cy="185" rx="80" ry="8" fill="url(#shadow)" />

      {/* Outer glow halo behind crystal */}
      <circle cx="200" cy="110" r="80" fill="url(#core)" opacity="0.55" style={{ filter: 'url(#blur)' }} />

      {/* Crystal — octahedron-ish bipyramid (8 visible facets) */}
      <g style={{ transformOrigin: '200px 110px' }}>
        {/* Left back facet */}
        <polygon points="200,40 140,110 200,110" fill="url(#facetL)" stroke="rgba(124,58,237,0.25)" strokeWidth="0.6" />
        {/* Right back facet */}
        <polygon points="200,40 260,110 200,110" fill="url(#facetR)" stroke="rgba(124,58,237,0.25)" strokeWidth="0.6" />
        {/* Left front facet */}
        <polygon points="200,110 140,110 200,180" fill="url(#facetT)" stroke="rgba(124,58,237,0.3)" strokeWidth="0.6" />
        {/* Right front facet */}
        <polygon points="200,110 260,110 200,180" fill="url(#facetB)" stroke="rgba(124,58,237,0.3)" strokeWidth="0.6" />
        {/* Side highlight (top ridge) */}
        <polygon points="200,40 205,45 200,110 195,45" fill="rgba(255,255,255,0.9)" />
      </g>

      {/* Specular highlight */}
      <ellipse cx="180" cy="72" rx="14" ry="4" fill="rgba(255,255,255,0.85)" transform="rotate(-30 180 72)" style={{ filter: 'url(#blur)' }} />

      {/* Inner violet core */}
      <circle cx="200" cy="110" r="14" fill="url(#core)" style={{ animation: 'coreBeat 2.2s ease-in-out infinite' }} />

      {/* Light rays */}
      <g opacity="0.5" style={{ mixBlendMode: 'screen' }}>
        <line x1="200" y1="110" x2="320" y2="30" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
        <line x1="200" y1="110" x2="80" y2="200" stroke="rgba(196,181,253,0.5)" strokeWidth="0.8" />
        <line x1="200" y1="110" x2="350" y2="180" stroke="rgba(167,139,250,0.5)" strokeWidth="0.8" />
      </g>

      {/* Tiny orbit dots */}
      <g style={{ animation: 'rotSlow 20s linear infinite', transformOrigin: '200px 110px' }}>
        <circle cx="290" cy="110" r="2" fill="#7C3AED" />
        <circle cx="110" cy="110" r="1.5" fill="#A78BFA" />
        <circle cx="200" cy="20" r="1.5" fill="#EC4899" />
      </g>
    </svg>
  );
}

/* ── Provenance panel ── */
function ProvenancePanel() {
  const hashRef = useRef(null);
  useEffect(() => {
    const hash = '0x9f86d081884c7d659a2feaa0c55ad015a3bf4f1b';
    let i = 14;
    const id = setInterval(() => {
      if (!hashRef.current) return;
      i = Math.min(hash.length, i + 1);
      if (i === hash.length) i = 14;
      hashRef.current.textContent = hash.slice(0, i) + (i < hash.length ? '▊' : '');
    }, 90);
    return () => clearInterval(id);
  }, []);

  return (
    <aside style={{
      borderLeft: '1px solid rgba(255,255,255,0.05)',
      background: 'transparent',
      padding: '18px 16px',
      display: 'flex', flexDirection: 'column', gap: '16px',
    }}>
      {/* Panel header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '9.5px', fontWeight: 700,
          letterSpacing: '0.24em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.6)',
        }}>Provenance</span>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          padding: '3px 7px', borderRadius: '4px',
          background: 'rgba(124,58,237,0.08)',
        }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#C4B5FD" strokeWidth="2">
            <circle cx="12" cy="12" r="9" /><path d="M3 12h18 M12 3a15 15 0 0 1 0 18 M12 3a15 15 0 0 0 0 18" />
          </svg>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '8.5px', fontWeight: 700,
            color: '#C4B5FD', letterSpacing: '0.14em',
          }}>L1</span>
        </div>
      </div>

      <div style={{
        padding: '12px',
        background: 'rgba(124,58,237,0.05)',
        border: '1px solid rgba(124,58,237,0.3)',
        borderRadius: '10px',
        boxShadow: '0 4px 12px -6px rgba(124,58,237,0.4)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '10px',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#ECFDF5" stroke="#059669" strokeWidth="1.5" />
              <path d="M8 12l3 3 5-6" stroke="#059669" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '10px', fontWeight: 700,
              letterSpacing: '0.22em', color: '#047857',
            }}>ANCHORED</span>
          </div>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '9px', color: 'rgba(255,255,255,0.5)',
          }}>2s ago</span>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 10px', borderRadius: '7px',
          background: 'rgba(124,58,237,0.1)',
        }}>
          <div ref={hashRef} style={{
            flex: 1,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '10.5px', color: '#D8B4FE',
            wordBreak: 'break-all', lineHeight: 1.5,
          }} />
          <button style={{ ...iconBtn, width: 22, height: 22, background: 'transparent', border: 'none' }} title="Copy">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
        </div>
        <a style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          marginTop: '8px',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '9.5px', color: '#7C3AED', fontWeight: 600,
          letterSpacing: '0.14em', textDecoration: 'none',
        }}>
          VIEW ON EXPLORER
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7M8 7h9v9" />
          </svg>
        </a>
      </div>

      {/* Metadata rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
        {[
          { k: 'BLOCK', v: '#1,492,087' },
          { k: 'FINALITY', v: '0.41s' },
          { k: 'NETWORK', v: 'DAGCHAIN L1' },
          { k: 'GAS USED', v: '0.0004 DAG' },
          { k: 'SIZE', v: '3.4 MB' },
        ].map(r => (
          <div key={r.k} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0 2px',
          }}>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '9.5px', letterSpacing: '0.2em',
              color: 'rgba(255,255,255,0.5)',
            }}>{r.k}</span>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '11px', color: '#FFFFFF', fontWeight: 500,
            }}>{r.v}</span>
          </div>
        ))}
      </div>

      {/* Owner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '9px',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: '"Inter", sans-serif', fontSize: '10px', fontWeight: 700,
          color: '#FFFFFF',
        }}>AD</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '11.5px', fontWeight: 500,
            color: '#FFFFFF',
          }}>Owner</div>
          <div style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '10px', color: 'rgba(255,255,255,0.6)',
          }}>0x4D2a…F3b1</div>
        </div>
      </div>

      {/* Signed quote */}
      <div style={{
        marginTop: 'auto',
        padding: '11px 13px',
        background: 'rgba(124,58,237,0.1)',
        border: '1px solid rgba(124,58,237,0.25)',
        borderRadius: '10px',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#C4B5FD" style={{ marginBottom: 6, opacity: 0.8 }}>
          <path d="M10 11H6c0-2 1-3 3-3V5c-3 0-5 2-5 6v6h6v-6zm10 0h-4c0-2 1-3 3-3V5c-3 0-5 2-5 6v6h6v-6z" />
        </svg>
        <div style={{
          fontFamily: '"Nasalization", serif', fontStyle: 'italic',
          fontWeight: 300, fontSize: '12.5px',
          color: '#E9D5FF', lineHeight: 1.5,
        }}>Immutable & verifiably yours — forever.</div>
      </div>
    </aside>
  );
}

/* ── tokens ── */
const pill = {
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  padding: '4px 9px', borderRadius: '999px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  fontFamily: '"Inter", sans-serif',
  fontSize: '10.5px', color: '#FFFFFF',
};
const iconBtn = {
  width: 26, height: 26, borderRadius: '6px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.05)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background 0.2s, border-color 0.2s',
};

/* ── helpers ── */
const LineMask = React.forwardRef(function LineMask({ children }, ref) {
  return (
    <span style={{ display: 'block', overflow: 'hidden', paddingBottom: '0.9em', marginBottom: '-0.9em' }}>
      <span ref={ref} style={{ display: 'inline-block', paddingBottom: '0.5em' }}>{children}</span>
    </span>
  );
});

function Sparkle({ size = 12, violet = false }) {
  const s = typeof size === 'number' ? `${size}px` : size;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <path
        d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z"
        fill={violet ? '#7C3AED' : '#0A0A0A'}
      />
    </svg>
  );
}

const ctaDark = {
  display: 'inline-flex', alignItems: 'center', gap: '10px',
  padding: '15px 30px', borderRadius: '999px',
  background: '#0A0A0A', color: '#FFFFFF',
  fontFamily: '"Inter", sans-serif',
  fontSize: '14px', fontWeight: 500,
  letterSpacing: '-0.005em', textDecoration: 'none',
  transition: 'background 0.3s, transform 0.3s',
};
const ctaGhost = {
  display: 'inline-flex', alignItems: 'center',
  padding: '15px 28px', borderRadius: '999px',
  border: '1px solid rgba(10,10,10,0.15)',
  background: 'transparent', color: '#0A0A0A',
  fontFamily: '"Inter", sans-serif',
  fontSize: '14px', fontWeight: 500,
  textDecoration: 'none',
  transition: 'border-color 0.3s, color 0.3s',
};
const ctaViolet = {
  display: 'inline-flex', alignItems: 'center', gap: '10px',
  padding: '15px 28px', borderRadius: '999px',
  background: 'rgba(124,58,237,0.15)',
  border: '1px solid rgba(124,58,237,0.5)',
  color: '#C4B5FD',
  fontFamily: '"Inter", sans-serif',
  fontSize: '14px', fontWeight: 500,
  letterSpacing: '-0.005em', textDecoration: 'none',
  transition: 'background 0.3s, color 0.3s, transform 0.3s, box-shadow 0.3s',
};
